/**
 * ChatFullPage Component
 * Full-page AI assistant interface.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../hooks/useChat';
import { useAuth } from '../../contexts/AuthContext';
import ChatLayout from './ChatLayout';
import Sidebar from './Sidebar';
import './ChatFullPage.css';

// Import MessageBubble and WelcomeScreen from ChatWidget
// For now, we'll redefine them here (you can extract to shared file later)

interface MessageBubbleProps {
  message: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    intent?: string;
    createdAt: string;
  };
  onCopy?: (text: string, id: string) => void;
  copiedId?: string | null;
  onReaction?: (messageId: string, reaction: 'up' | 'down') => void;
  onSuggestionClick?: (suggestion: string) => void;
  onActionClick?: (action: string) => void;
  onEdit?: (text: string) => void;
  onSendMessage?: (message: string) => void;
}

const formatRichMarkdown = (text: string): string => {
  const escapeHtml = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const inline = (value: string) => escapeHtml(value)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');

  const isSeparator = (line: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
  const splitCells = (line: string) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
  const alignFor = (cell: string) => {
    const value = cell.trim();
    if (value.startsWith(':') && value.endsWith(':')) return 'center';
    if (value.endsWith(':')) return 'right';
    return 'left';
  };

  const lines = String(text || '').split('\n');
  const html: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const next = lines[i + 1];

    if (line.includes('|') && next && isSeparator(next)) {
      const headers = splitCells(line);
      const aligns = splitCells(next).map(alignFor);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      i -= 1;

      html.push('<div class="chat-md-table-wrap"><table class="chat-md-table"><thead><tr>');
      headers.forEach((header, index) => {
        html.push(`<th style="text-align:${aligns[index] || 'left'}">${inline(header)}</th>`);
      });
      html.push('</tr></thead><tbody>');
      rows.forEach(row => {
        html.push('<tr>');
        headers.forEach((_, index) => {
          html.push(`<td style="text-align:${aligns[index] || 'left'}">${inline(row[index] || '')}</td>`);
        });
        html.push('</tr>');
      });
      html.push('</tbody></table></div>');
      continue;
    }

    if (!line.trim()) {
      html.push('<div class="chat-md-space"></div>');
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 4);
      html.push(`<h${level} class="chat-md-heading chat-md-heading-${level}">${inline(heading[2])}</h${level}>`);
      continue;
    }

    const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
    if (numbered) {
      html.push(`<div class="chat-md-list-row"><span>${numbered[1]}.</span><p>${inline(numbered[2])}</p></div>`);
      continue;
    }

    const bullet = line.match(/^\s*[-•]\s+(.+)$/);
    if (bullet) {
      html.push(`<div class="chat-md-list-row"><span>•</span><p>${inline(bullet[1])}</p></div>`);
      continue;
    }

    html.push(`<p class="chat-md-paragraph">${inline(line)}</p>`);
  }

  return html.join('');
};

const getTokenUsage = (usage: any) => {
  if (!usage) return null;
  const inputTokens = usage.inputTokens ?? usage.input;
  const outputTokens = usage.outputTokens ?? usage.output;
  const totalTokens = usage.totalTokens ?? (
    inputTokens != null || outputTokens != null
      ? Number(inputTokens || 0) + Number(outputTokens || 0)
      : undefined
  );

  return {
    inputTokens,
    outputTokens,
    totalTokens,
    estimated: Boolean(usage.estimated),
    semanticCacheHit: Boolean(usage.semanticCacheHit),
  };
};

const TokenUsageLine: React.FC<{ usage: any }> = ({ usage }) => {
  const tokens = getTokenUsage(usage);
  if (!tokens || (tokens.inputTokens == null && tokens.outputTokens == null)) return null;

  return (
    <div className="chat-token-usage">
      Input tokens: {tokens.inputTokens ?? 0} | Output tokens: {tokens.outputTokens ?? 0} | Total tokens: {tokens.totalTokens ?? 0}
      {tokens.semanticCacheHit ? ' | cached' : ''}
      {tokens.estimated && !tokens.semanticCacheHit ? ' | estimated' : ''}
    </div>
  );
};

const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({ 
  message, 
  onActionClick,
  onEdit,
  onSendMessage
}) => {
  // Simple markdown formatter
  const formatMarkdown = (text: string): string => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^\s*(\d+)\.\s+/gm, '<br/>$1. ')
      .replace(/^\s*-\s+/gm, '<br/>• ')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  void formatMarkdown;

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parsed: any = useMemo(() => {
    if (message.role === 'user') return null;
    try {
      return typeof message.content === 'string'
        ? JSON.parse(message.content)
        : message.content;
    } catch (error) {
      return { type: 'text', message: message.content };
    }
  }, [message.content, message.role]);

  const renderedMessageHtml = useMemo(
    () => formatRichMarkdown(parsed?.message || ''),
    [parsed?.message]
  );

  // Render user message (simple text)
  if (message.role === 'user') {
    return (
      <div className="message-bubble user">
        <div className="message-content">
          <div className="message-text">{message.content}</div>
        </div>
        <div className="message-footer">
          <div className="message-timestamp">{formatTime(message.createdAt)}</div>
          <div className="message-actions-row">
            <button className="msg-action-btn" onClick={() => onEdit?.(message.content)} title="Edit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button className="msg-action-btn" onClick={() => {
              const blob = new Blob([message.content], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `message-${message.id}.txt`;
              a.click();
              URL.revokeObjectURL(url);
            }} title="Download">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render clean response with expense details
  if (!parsed) {
    return (
      <div className="message-bubble assistant">
        <div className="message-content">
          <div className="response-message-clean">{message.content}</div>
        </div>
        <div className="message-footer">
          <div className="message-timestamp">{formatTime(message.createdAt)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="message-bubble assistant">
      <div className="message-content">
        {parsed.title && (
          <div className="response-title-clean">{parsed.title}</div>
        )}

        {/* Always show the markdown message when available */}
        {parsed.message && (
          <div className="response-message-clean" dangerouslySetInnerHTML={{ __html: renderedMessageHtml }} />
        )}

        {/* Show summary if available and no message */}
        {!parsed.message && parsed.summary && (
          <div className="response-summary-clean">
            Found {parsed.summary.totalCount || parsed.summary.count || 0} expenses • Total: {parsed.summary.currency || '₹'}{parsed.summary.totalAmount || parsed.summary.total_amount || 0}
          </div>
        )}
        
        {/* Show expense items only if no markdown message */}
        {!parsed.message && parsed.items && parsed.items.length > 0 && (
          <div className="expense-items-clean">
            {parsed.items.map((expense: any, index: number) => (
              <div key={expense.id || index} className="expense-item-clean">
                <div className="expense-item-header">
                  <div className="expense-item-title">{expense.title || 'Untitled'}</div>
                  <div className={`expense-item-status status-${(expense.status || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}>
                    {expense.status || 'Unknown'}
                  </div>
                </div>
                <div className="expense-item-details">
                  <div className="expense-item-row">
                    <span className="expense-item-label">Amount:</span>
                    <span className="expense-item-value expense-amount">{expense.currency || '₹'}{expense.amount ?? 0}</span>
                  </div>
                  <div className="expense-item-row">
                    <span className="expense-item-label">Date:</span>
                    <span className="expense-item-value">{expense.date ? new Date(expense.date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="expense-item-row">
                    <span className="expense-item-label">Category:</span>
                    <span className="expense-item-value">{expense.category || 'N/A'}</span>
                  </div>
                  {expense.description && (
                    <div className="expense-item-row">
                      <span className="expense-item-label">Description:</span>
                      <span className="expense-item-value">{expense.description}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Action buttons if available */}
        {parsed.primary_actions && parsed.primary_actions.length > 0 && (
          <div className="action-buttons-clean">
            {parsed.primary_actions.map((action: any, index: number) => (
              <button
                key={index}
                className="action-btn-clean primary"
                onClick={() => onActionClick?.(action.action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        
        {parsed.secondary_actions && parsed.secondary_actions.length > 0 && (
          <div className="action-buttons-clean secondary">
            {parsed.secondary_actions.map((action: any, index: number) => (
              <button
                key={index}
                className="action-btn-clean"
                onClick={() => onActionClick?.(action.action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        
        {/* AI Guidance */}
        {parsed.ai_guidance && (
          <div className="ai-guidance-clean">
            {parsed.ai_guidance}
          </div>
        )}
        
        {/* Suggestions under the message */}
        {parsed.suggestions && parsed.suggestions.length > 0 && (
          <div className="response-suggestions-clean">
            {parsed.suggestions.map((suggestion: any, index: number) => (
              <button
                key={index}
                className="suggestion-chip-clean"
                onClick={() => onSendMessage?.(suggestion.query || suggestion.label || suggestion)}
              >
                {suggestion.label || suggestion.query || suggestion}
              </button>
            ))}
          </div>
        )}

        <TokenUsageLine usage={parsed.usage} />
        {false && parsed.usage && (parsed.usage.input != null || parsed.usage.output != null) && (
          <div className="chat-token-usage">
            Input tokens: {parsed.usage.input ?? 0} · Output tokens: {parsed.usage.output ?? 0}
          </div>
        )}
      </div>
      <div className="message-footer">
        <div className="message-timestamp">{formatTime(message.createdAt)}</div>
        <div className="message-actions-row">
          <button className="msg-action-btn" onClick={() => {
            const text = parsed.message || parsed.items?.map((i: any) => `${i.title}: ${i.currency || '₹'}${i.amount} (${i.status})`).join('\n') || message.content;
            navigator.clipboard.writeText(text);
          }} title="Copy">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
          <button className="msg-action-btn" onClick={() => {
            const text = parsed.message || JSON.stringify(parsed, null, 2);
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `response-${message.id}.txt`;
            a.click();
            URL.revokeObjectURL(url);
          }} title="Download">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = React.memo(
  MessageBubbleComponent,
  (previous, next) =>
    previous.message.id === next.message.id &&
    previous.message.content === next.message.content &&
    previous.message.createdAt === next.message.createdAt
);

const WelcomeScreen: React.FC<{ onSuggestionClick: (text: string) => void; userName?: string }> = ({ onSuggestionClick, userName }) => (
  <div className="welcome-screen-fullpage">
    <div className="welcome-robot-container">
      <div className="robot-emoji">🤖</div>
      <div className="robot-chat-bubble">
        <div className="bubble-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
    <h1 className="welcome-title-large">Hi! I'm Creyon <span aria-hidden="true">👋</span></h1>
    <p className="welcome-subtitle">Welcome back, {userName || 'User'}</p>
    <div className="welcome-suggestions-grid">
      <button className="suggestion-card" onClick={() => onSuggestionClick('Show my expenses')}>
        <div className="suggestion-card-icon" aria-hidden="true">💰</div>
        <div className="suggestion-card-content">
          <div className="suggestion-card-title">My Expenses</div>
          <div className="suggestion-card-desc">View and manage your expense reports</div>
        </div>
      </button>
      <button className="suggestion-card" onClick={() => onSuggestionClick('Show pending approvals')}>
        <div className="suggestion-card-icon" aria-hidden="true">⏰</div>
        <div className="suggestion-card-content">
          <div className="suggestion-card-title">Pending Approvals</div>
          <div className="suggestion-card-desc">Review expenses waiting for approval</div>
        </div>
      </button>
      <button className="suggestion-card" onClick={() => onSuggestionClick('Show my team members')}>
        <div className="suggestion-card-icon" aria-hidden="true">👥</div>
        <div className="suggestion-card-content">
          <div className="suggestion-card-title">Team Members</div>
          <div className="suggestion-card-desc">View your team and their details</div>
        </div>
      </button>
      <button className="suggestion-card" onClick={() => onSuggestionClick('Help')}>
        <div className="suggestion-card-icon" aria-hidden="true">❓</div>
        <div className="suggestion-card-content">
          <div className="suggestion-card-title">Get Help</div>
          <div className="suggestion-card-desc">Learn what I can do for you</div>
        </div>
      </button>
    </div>
  </div>
);

const ChatFullPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { messages, isLoading, error, sendMessage, loadChat, clearChat } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Handle action button clicks
  const handleActionClick = (action: string) => {
    // Close current tab and navigate in opener window
    if (window.opener && !window.opener.closed) {
      // Navigate in the parent window
      switch (action) {
        case 'view_all_expenses':
        case 'submit_expense':
          window.opener.location.href = '/dashboard';
          break;
        case 'view_performance':
          window.opener.location.href = '/performance/objectives';
          break;
        case 'view_nominations':
          window.opener.location.href = '/nominations/feed';
          break;
        case 'view_timesheets':
          window.opener.location.href = '/timesheet';
          break;
        case 'filter_expenses':
        case 'export_expenses':
          window.opener.location.href = '/dashboard';
          break;
      }
      // Close this chat window
      window.close();
    } else {
      // No opener, navigate in same window
      switch (action) {
        case 'view_all_expenses':
        case 'submit_expense':
          navigate('/dashboard');
          break;
        case 'view_performance':
          navigate('/performance/objectives');
          break;
        case 'view_nominations':
          navigate('/nominations/feed');
          break;
        case 'view_timesheets':
          navigate('/timesheet');
          break;
        case 'filter_expenses':
        case 'export_expenses':
          navigate('/dashboard');
          break;
      }
    }
  };

  // Load chat history from localStorage — keyed by user ID so each user has their own history
  useEffect(() => {
    if (!user?.id) return;
    const storageKey = `creon_chat_history_${user.id}`;
    const loadChatHistory = () => {
      try {
        // One-time migration: move old shared key to user-specific key
        const oldKey = 'creon_chat_history';
        const oldStored = localStorage.getItem(oldKey);
        if (oldStored && !localStorage.getItem(storageKey)) {
          localStorage.setItem(storageKey, oldStored);
          localStorage.removeItem(oldKey);
          console.log('[ChatFullPage] Migrated chat history to user-specific key');
        }

        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const history = JSON.parse(stored);
          const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
          const filtered = history.filter((chat: any) => 
            new Date(chat.lastUpdated).getTime() > thirtyDaysAgo
          );
          setChatHistory(filtered);
          if (filtered.length !== history.length) {
            localStorage.setItem(storageKey, JSON.stringify(filtered));
          }

          // Restore the last active session so the user continues where they left off
          const lastSessionKey = `creon_last_session_${user.id}`;
          const lastSessionId = localStorage.getItem(lastSessionKey);
          console.log('[ChatContinuity][FullChatRestoreStart]', {
            storageKey,
            lastSessionKey,
            lastSessionId,
            availableChatIds: filtered.map((chat: any) => chat.id),
            availableCount: filtered.length,
          });
          if (lastSessionId) {
            const lastChat = filtered.find((c: any) => c.id === lastSessionId);
            if (lastChat) {
              const restoredSessionId = lastChat.backendSessionId || lastChat.sessionId || null;
              const userMsgCount = lastChat.messages?.filter((m: any) => m.role === 'user').length || 0;
              const assistantMsgCount = lastChat.messages?.filter((m: any) => m.role === 'assistant').length || 0;
              console.log('[ChatContinuity][FullChatRestoreSuccess]', {
                restoredChatId: lastSessionId,
                restoredSessionId,
                messageCount: lastChat.messages?.length || 0,
                userMessages: userMsgCount,
                assistantMessages: assistantMsgCount,
                hasResponses: assistantMsgCount > 0,
              });
              setCurrentChatId(lastSessionId);
              loadChat(lastSessionId, lastChat.messages, restoredSessionId);
              console.log('[ChatFullPage] Restored last session:', lastSessionId, 'with', lastChat.messages.length, 'messages');
            } else {
              console.log('[ChatContinuity][FullChatRestoreMiss]', {
                lastSessionId,
                availableChatIds: filtered.map((chat: any) => chat.id),
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };
    loadChatHistory();
  }, [user?.id, loadChat]);

  // Save current chat to history — keyed by user ID
  useEffect(() => {
    if (messages.length > 0 && user?.id) {
      const storageKey = `creon_chat_history_${user.id}`;
      const lastSessionKey = `creon_last_session_${user.id}`;
      const chatTitle = messages[0]?.content.substring(0, 50) || 'New Chat';
      const chatId = currentChatId || `chat_${Date.now()}`;
      const backendSessionId = [...messages].reverse().find(message => message.sessionId)?.sessionId || null;

      // Fix: persist the generated chatId so subsequent saves (e.g. when assistant
      // response arrives) update the same entry instead of creating a new one.
      if (!currentChatId) {
        setCurrentChatId(chatId);
      }

      console.log('[ChatContinuity][FullChatSaveBefore]', {
        currentChatId,
        chatId,
        backendSessionId,
        messageCount: messages.length,
      });
      
      // Compact assistant messages for storage — keep full content but cap
      // individual message size to avoid localStorage quota overflow.
      const compactMessages = messages.map(msg => {
        if (msg.role === 'user') return msg;
        // For assistant messages, keep content as-is if it's small enough.
        // If it's very large (>10KB), it's likely a full JSON response with items.
        // We still store the full content — the real quota protection is limiting
        // total chat count below.
        return msg;
      });

      const chatData = {
        id: chatId,
        title: chatTitle,
        messages: compactMessages,
        backendSessionId,
        lastUpdated: new Date().toISOString(),
        messageCount: messages.length
      };

      setChatHistory((previousHistory) => {
        const updatedHistory = [...previousHistory];
        const existingIndex = updatedHistory.findIndex(c => c.id === chatId);

        if (existingIndex >= 0) {
          updatedHistory[existingIndex] = chatData;
        } else {
          updatedHistory.unshift(chatData);
        }

        // Keep only the 20 most recent chats to prevent localStorage overflow
        const trimmedHistory = updatedHistory.slice(0, 20);

        try {
          const serialized = JSON.stringify(trimmedHistory);
          localStorage.setItem(storageKey, serialized);
          localStorage.setItem(lastSessionKey, chatId);
          console.log('[ChatContinuity][FullChatSaveAfter]', {
            savedChatId: chatId,
            backendSessionId,
            messageCount: messages.length,
            storageBytes: serialized.length,
          });
        } catch (error) {
          console.error('Failed to save chat history:', error);
          // If quota exceeded, remove oldest chats and retry
          if ((error as any)?.name === 'QuotaExceededError' || (error as any)?.code === 22) {
            try {
              const reduced = trimmedHistory.slice(0, 10);
              localStorage.setItem(storageKey, JSON.stringify(reduced));
              console.warn('[ChatContinuity] Reduced history to 10 chats due to quota');
            } catch (retryError) {
              console.error('[ChatContinuity] Still failed after reducing:', retryError);
            }
          }
        }

        return trimmedHistory;
      });
      setCurrentChatId(chatId);
    }
  }, [messages, currentChatId, user?.id]);

  // Load chat from history
  const handleLoadChat = (chatId: string) => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      setCurrentChatId(chatId);
      // Load the messages from the chat history
      loadChat(chatId, chat.messages, chat.backendSessionId || chat.sessionId || null);
      console.log('[ChatFullPage] Loaded chat:', chatId, 'with', chat.messages.length, 'messages');
    }
  };

  // New chat handler
  const handleNewChat = () => {
    setCurrentChatId(null);
    clearChat();
    // Clear the last session pointer so next login starts fresh
    if (user?.id) {
      localStorage.removeItem(`creon_last_session_${user.id}`);
    }
    console.log('[ChatFullPage] Started new chat');
  };

  // Delete chat
  const handleDeleteChat = (chatId: string) => {
    const updated = chatHistory.filter(c => c.id !== chatId);
    setChatHistory(updated);
    if (user?.id) {
      const storageKey = `creon_chat_history_${user.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updated));
      // Clear last session pointer if deleting the active session
      const lastSessionKey = `creon_last_session_${user.id}`;
      if (localStorage.getItem(lastSessionKey) === chatId) {
        localStorage.removeItem(lastSessionKey);
      }
    }
    if (currentChatId === chatId) {
      handleNewChat();
    }
  };

  // Edit chat title
  const handleEditChat = (chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
  };

  const handleSaveEdit = (chatId: string) => {
    const updated = chatHistory.map(chat => 
      chat.id === chatId ? { ...chat, title: editingTitle } : chat
    );
    setChatHistory(updated);
    if (user?.id) {
      localStorage.setItem(`creon_chat_history_${user.id}`, JSON.stringify(updated));
    }
    setEditingChatId(null);
    setEditingTitle('');
  };

  // Download chat
  const handleDownloadChat = (chatId: string) => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      const content = JSON.stringify(chat, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${chat.title.replace(/[^a-z0-9]/gi, '_')}_${chatId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Filter chats by search query
  const filteredChats = chatHistory.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isLoading]);

  // Focus input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      const maxTextareaHeight = 150;
      inputRef.current.style.height = 'auto';
      const nextHeight = Math.min(inputRef.current.scrollHeight, maxTextareaHeight);
      inputRef.current.style.height = `${nextHeight}px`;
      inputRef.current.style.overflowY = inputRef.current.scrollHeight > maxTextareaHeight ? 'auto' : 'hidden';
    }
  }, [inputValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    // Auto-start new chat after 20 messages
    if (messages.length >= 20) {
      handleNewChat();
    }

    const message = inputValue.trim();
    setInputValue('');
    
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const showWelcome = messages.length === 0 && !isLoading;

  return (
    <ChatLayout
      sidebar={(
        <Sidebar
          chats={filteredChats}
          currentChatId={currentChatId}
          editingChatId={editingChatId}
          editingTitle={editingTitle}
          searchQuery={searchQuery}
          onNewChat={handleNewChat}
          onLoadChat={handleLoadChat}
          onDeleteChat={handleDeleteChat}
          onEditChat={handleEditChat}
          onDownloadChat={handleDownloadChat}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={() => setEditingChatId(null)}
          onEditingTitleChange={setEditingTitle}
          onSearchQueryChange={setSearchQuery}
        />
      )}
    >
      <div className="chat-fullpage-main">
        <button
          className="chat-fullpage-close-btn"
          onClick={() => {
            if (window.opener && !window.opener.closed) {
              window.close();
            } else {
              navigate('/home');
            }
          }}
          aria-label="Close"
          title="Close"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="chat-fullpage-header">
          <div>
            <p className="chat-fullpage-eyebrow">AI Assistant</p>
            <h1 className="chat-fullpage-title">Chatbot</h1>
          </div>
          <button className="chat-fullpage-header-action" onClick={handleNewChat}>
            New Chat
          </button>
        </div>

        {/* Messages */}
        <div className="chat-fullpage-messages" ref={messagesContainerRef}>
          {showWelcome ? (
            <WelcomeScreen onSuggestionClick={handleSuggestionClick} userName={user?.name} />
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble 
                  key={message.id} 
                  message={message} 
                  onSuggestionClick={handleSuggestionClick}
                  onActionClick={handleActionClick}
                  onEdit={(text) => {
                    setInputValue(text);
                    inputRef.current?.focus();
                  }}
                  onSendMessage={(msg) => sendMessage(msg)}
                />
              ))}
              
              {isLoading && (
                <div className="message-bubble assistant">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="chat-error-message">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Box */}
        <div className="chat-fullpage-input-container">
          <form className="chat-fullpage-input-form" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message Creyon..."
              disabled={isLoading}
              className="chat-fullpage-textarea"
              rows={1}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="chat-fullpage-send-btn"
              aria-label="Send message"
            >
              {isLoading ? (
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>
    </ChatLayout>
  );
};

export default ChatFullPage;
